import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import dashboardApi, { ApiError } from '../apis/apis';

// Create the context
const SessionStatsContext = createContext(null);

// Custom hook to use the session stats context
export const useSessionStats = () => {
  const context = useContext(SessionStatsContext);
  if (!context) {
    throw new Error('useSessionStats must be used within a SessionStatsProvider');
  }
  return context;
};

// Provider component
export const SessionStatsProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [sessionStats, setSessionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isInvalidSession, setIsInvalidSession] = useState(false);

  useEffect(() => {
    const fetchSessionStats = async () => {
      // Get session_id from URL query parameters
      const sessionIdFromUrl = searchParams.get('session_id');
      
      if (!sessionIdFromUrl) {
        setSessionId(null);
        setSessionStats(null);
        setError(null);
        setIsInvalidSession(false);
        setLoading(false);
        return;
      }

      setSessionId(sessionIdFromUrl);
      setLoading(true);
      setError(null);
      setIsInvalidSession(false);

      try {
        const response = await dashboardApi.getSessionStats(sessionIdFromUrl);
        
        // Check if the response is an empty object or has no meaningful data
        const data = response.data;
        const isEmptyResponse = !data || 
          Object.keys(data).length === 0 || 
          (!data.session_id && !data.aggregated && !data.question_statistics);
        
        if (isEmptyResponse) {
          console.log('📦 API: Empty response received for session ID:', sessionIdFromUrl);
          setIsInvalidSession(true);
          setSessionStats(null);
          setError(null);
        } else {
          setSessionStats(data);
          setIsInvalidSession(false);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching session stats:', err);
        setError(err instanceof ApiError ? err : new Error('Failed to fetch session statistics'));
        setSessionStats(null);
        setIsInvalidSession(false);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionStats();
  }, [searchParams]);

  // Refresh function to manually refetch data
  const refreshSessionStats = async () => {
    if (!sessionId) {
      return;
    }

    setLoading(true);
    setError(null);
    setIsInvalidSession(false);

    try {
      const response = await dashboardApi.getSessionStats(sessionId);
      
      // Check if the response is an empty object or has no meaningful data
      const data = response.data;
      const isEmptyResponse = !data || 
        Object.keys(data).length === 0 || 
        (!data.session_id && !data.aggregated && !data.question_statistics);
      
      if (isEmptyResponse) {
        console.log('📦 API: Empty response received for session ID:', sessionId);
        setIsInvalidSession(true);
        setSessionStats(null);
        setError(null);
      } else {
        setSessionStats(data);
        setIsInvalidSession(false);
        setError(null);
      }
    } catch (err) {
      console.error('Error refreshing session stats:', err);
      setError(err instanceof ApiError ? err : new Error('Failed to refresh session statistics'));
      setIsInvalidSession(false);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    sessionStats,
    loading,
    error,
    sessionId,
    isInvalidSession,
    refreshSessionStats,
    // Convenience getters for commonly used data
    aggregated: sessionStats?.aggregated || null,
    subjectStatistics: sessionStats?.subject_statistics || [],
    questionStatistics: sessionStats?.question_statistics || [],
    sessionDate: sessionStats?.date || null,
    // Add flags to indicate which screen should be shown
    showNoSessionIdScreen: !loading && !sessionId && !error && !isInvalidSession,
    showNoValidSessionIdScreen: !loading && sessionId && isInvalidSession && !error
  };

  return (
    <SessionStatsContext.Provider value={value}>
      {children}
    </SessionStatsContext.Provider>
  );
};

